import { Column, RowData } from "@tanstack/react-table";
import { ColumnMeta } from "./data-table-types";

/**
 * Derives a stable, unique row ID from data objects.
 * Prefers `__typename:id` for GraphQL union types, falls back to `id` alone,
 * then to the array index as a last resort.
 */
export function defaultGetRowId<TData>(row: TData, index: number): string {
	if (typeof row === "object" && row !== null) {
		const r = row as Record<string, unknown>;
		let id: string | null = null;
		if (typeof r.id === "string") {
			id = r.id;
		} else if (typeof r.id === "number") {
			id = String(r.id);
		}
		if (id !== null) {
			const typename = typeof r.__typename === "string" ? r.__typename : null;
			return typename ? `${typename}:${id}` : id;
		}
	}
	return String(index);
}

/**
 * Reads a column's `meta` and fills in sensible defaults for every
 * display-related flag so callers never have to null-check.
 *
 * @typeParam TData      - Row data type forwarded from the table instance.
 * @typeParam TSortField - Union of valid sort-field strings for the table.
 * @param column - A TanStack Table `Column<TData>` whose `columnDef.meta`
 *                 may be partially defined (or absent).
 * @returns A fully-populated meta object
 *          (`Omit<Required<ColumnMeta<…>>, "sortField"> & ColumnMeta<…>`)
 *          where `align` defaults to `"left"`, `verticalAlign` to `"center"`,
 *          `sortable` to `false`, and `truncate` to `false`.
 *          The optional `sortField` is preserved as-is.
 */
export function getMetaWithDefaults<
	TData extends RowData,
	TSortField extends string,
>(
	column: Column<TData>,
): Omit<Required<ColumnMeta<TData, unknown, TSortField>>, "sortField"> &
	ColumnMeta<TData, unknown, TSortField> {
	const meta = column.columnDef.meta as
		| ColumnMeta<TData, unknown, TSortField>
		| undefined;

	return {
		...meta,
		align: meta?.align ?? "left",
		verticalAlign: meta?.verticalAlign ?? "center",
		sortable: meta?.sortable ?? false,
		truncate: meta?.truncate ?? false,
	};
}

/**
 * `defaultColumn` override that every table feeding `getColumnWidth` must pass to
 * `useReactTable`. It clears TanStack's built-in `size: 150` feature default so a
 * column's `columnDef.size` reflects only what the author declared. `getSize()`
 * still falls back to 150 for undeclared columns, so resolved layout widths are
 * unchanged — only the declared-vs-undeclared signal `getColumnWidth` reads changes.
 *
 * Drop this from a table and `getColumnWidth` silently hands every column a fixed
 * width; keep the two together.
 *
 * web-app cross-entity lists (Holding movements, Distributions, Holdings,
 * Orders) follow a stricter column-size convention on top of this mechanism:
 * see `apps/web-app/src/components/organisms/crossEntityTableConvention.ts`.
 */
export const AUTOSIZING_DEFAULT_COLUMN = { size: undefined } as const;

/**
 * Returns a pixel width string for columns that declare an explicit width, or
 * undefined for columns that leave their size unset so auto-sizing works.
 *
 * A column auto-sizes only when the author declared no `size` AND its resolved
 * width is TanStack's default (150). Two conditions, because each guards a
 * distinct column:
 *
 * - `columnDef.size === undefined` distinguishes an author-declared `size: 150`
 *   (which must keep its 150px width — the original bug) from an undeclared
 *   column. The table passes `AUTOSIZING_DEFAULT_COLUMN` so TanStack's feature
 *   default of 150 does not masquerade as an author-declared `columnDef.size`.
 * - `getSize() === 150` preserves the fixed width of columns that declare no
 *   `size` but resolve above 150 via `minSize` (e.g. `minSize: 300` → 300px).
 *   Those previously rendered at their clamped width and must not collapse.
 *
 * The result matches the pre-fix behaviour (px whenever the resolved size is not
 * 150) for every column except an explicit `size: 150`, which now keeps its width.
 *
 * @see https://github.com/TanStack/table/discussions/3192#discussioncomment-3873093
 */
export function getColumnWidth<TData extends RowData>(
	column: Column<TData>,
): string | undefined {
	if (column.columnDef.size === undefined && column.getSize() === 150) {
		return undefined;
	}
	return `${column.getSize()}px`;
}
